import React from "react";
import { Link, useLocation } from "react-router-dom";
import { LogOut, Star, LogIn, Activity } from "lucide-react";
import ChaosLogo from "@/components/ChaosLogo";
import { useAuth } from "@/contexts/AuthContext";
import useGuestSession from "@/hooks/useGuestSession";

/**
 * Wide top header used across auth + scan + profile views.
 * Slimmer than prior phases for de-scaled minimalist look. History toggle
 * lives in the workspace control bar inside `ScanInterface`, not in the header.
 */
const ChaosHeader = ({ showCredits = false, compact = false }) => {
  const { user, logout, status, openAuthModal } = useAuth();
  const { guest } = useGuestSession();
  // Authed users show their ledger balance; guests show their remaining
  // guest credit count (capped at 2 by `useGuestSession` — 1 scan cost).
  const credits =
    status === "authed" &&
    typeof user?.credits_ledger_balance === "number"
      ? user.credits_ledger_balance
      : (guest?.guestCredits ?? 2);
  const location = useLocation();
  const onProfile = location.pathname.startsWith("/profile");

  return (
    <header
      data-testid="site-header"
      className={`relative z-30 w-full border-b border-white/[0.06] bg-[#020617]/60 backdrop-blur-md ${
        compact ? "pb-2.5" : "pb-3.5"
      }`}
      style={{
        // Push content well below the iOS / Android status-bar notch when
        // running as an installed PWA so the brand line + counter have
        // proper breathing room from the native chrome.
        paddingTop: "calc(env(safe-area-inset-top) + 16px)",
      }}
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-10 flex items-center justify-between gap-1.5 sm:gap-3">
        <Link
          to="/"
          data-testid="header-brand-link"
          className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1 hover:opacity-90 transition-opacity"
        >
          <ChaosLogo size={compact ? 22 : 26} className="shrink-0" />
          <div className="flex items-center min-w-0 font-mono text-[9px] sm:text-[11px] tracking-[0.14em] sm:tracking-[0.18em] text-[#f1f5f9] uppercase">
            <span data-testid="header-title" className="truncate">
              <span className="text-[#cbd5e1]">CMH</span>
              <span className="text-[#FF0033] mx-1.5">//</span>
              <span className="text-[#f1f5f9] hidden md:inline">CHAOS MEDIA HUB</span>
              <span className="text-[#f1f5f9] md:hidden">HUB</span>
            </span>
          </div>
        </Link>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {status === "authed" && user?.email && (
            <div className="hidden md:flex flex-col items-end leading-tight">
              <span
                data-testid="header-user-email"
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#cbd5e1] truncate max-w-[220px]"
              >
                {user.email}
              </span>
              <Link
                to="/profile"
                data-testid="header-profile-link"
                className={`font-mono text-[9px] uppercase tracking-[0.24em] transition-colors ${
                  onProfile
                    ? "text-[#FF0033]"
                    : "text-[#64748b] hover:text-[#FF0033]"
                }`}
              >
                My profile / billing →
              </Link>
            </div>
          )}

          {showCredits && (
            <div
              data-testid="credit-badge"
              className="flex items-center gap-1 sm:gap-1.5 border border-[#FF0033]/50 bg-[#FF0033]/[0.08] text-[#FF0033] px-1.5 sm:px-2.5 py-1 font-mono text-[9px] sm:text-[10px] uppercase tracking-wider sm:tracking-widest"
            >
              <span
                className="inline-block w-1.5 h-1.5 bg-[#FF0033] chaos-pulse"
                aria-hidden
              />
              <span className="text-[#cbd5e1]/80 hidden sm:inline">
                Credits:
              </span>
              <span className="text-[#cbd5e1]/80 sm:hidden">CREDS</span>
              <span
                data-testid="credit-count"
                className="text-[#FF0033] font-bold tabular-nums"
              >
                {credits}
              </span>
            </div>
          )}

          {status === "authed" && (
            <Link
              to="/updates"
              data-testid="header-updates-link"
              title="Operator changelog"
              aria-label="Operator updates"
              className="inline-flex items-center gap-1.5 border border-white/[0.12] hover:border-[#FF0033]/60 text-[#cbd5e1] hover:text-[#FF0033] px-2 sm:px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors"
            >
              <Activity size={12} className="text-[#FF0033]" />
              <span className="hidden md:inline">Updates</span>
            </Link>
          )}

          <Link
            to="/review"
            data-testid="header-feedback-link"
            title="Leave Feedback"
            aria-label="Leave Feedback"
            className="inline-flex items-center gap-1.5 border border-white/[0.12] hover:border-amber-300/70 text-[#cbd5e1] hover:text-amber-300 px-2 sm:px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors"
          >
            <Star size={12} className="text-amber-300" />
            <span className="hidden md:inline">Leave Feedback</span>
          </Link>

          {status !== "authed" && (
            <button
              type="button"
              data-testid="header-signin-button"
              onClick={() =>
                openAuthModal({ reason: "Sign in to unlock 7 free credits." })
              }
              className="inline-flex items-center gap-1.5 border border-[#FF0033] bg-[#FF0033]/10 hover:bg-[#FF0033] text-[#FF0033] hover:text-white px-2.5 sm:px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] font-bold transition-colors"
              title="Sign in to unlock 7 free credits"
              aria-label="Sign in"
            >
              <LogIn size={12} />
              <span>Sign in</span>
            </button>
          )}

          {status === "authed" && (
            <>
              <Link
                to="/profile"
                data-testid="header-profile-link-mobile"
                className="md:hidden inline-flex items-center border border-white/[0.12] hover:border-[#FF0033] text-[#cbd5e1] hover:text-[#FF0033] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors"
                title="Profile / Billing"
                aria-label="Profile and billing"
              >
                Profile
              </Link>
              <button
                type="button"
                data-testid="logout-button"
                onClick={logout}
                className="inline-flex items-center gap-1.5 border border-white/[0.12] hover:border-[#FF0033] text-[#cbd5e1] hover:text-[#FF0033] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors"
                title="Sign out"
              >
                <LogOut size={12} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default ChaosHeader;
